/**
 * Social card templates — derived directly from the Figma file
 * "weesper-radio-social-identity".
 *
 * Two layouts per platform:
 *   - `guest`    — artist photo + name (frames lbigpost / lbsc)
 *   - `resident` — same, plus a show/programme name banner at the top
 *                  (frames ABE IG / ABE SC). Per the brief, resident cards
 *                  REUSE the guest navy+diamond back plate (not the resident
 *                  design's own grey dotted pattern) and a thinner 2px border.
 *
 * Layering (back to front):
 *   1. `assets.back`   — navy + halftone pattern (opaque)
 *   2. artist photo    — clipped to `photoFrame`, drag + zoom
 *   3. `border`        — orange frame on top of the photo (INSIDE align)
 *   4. `assets.logo`   — RADIO WEESPER furniture (transparent PNG)
 *   5. name panel      — navy box + orange border + connector + artist name
 *   6. show banner     — resident only: navy box + border + show name (top)
 */

export const COLORS = {
  navy: '#000b35',
  /** Resident-layout base colour (replaces navy for its fills + background). */
  grey: '#2d2d2d',
  orange: '#ff4e00',
} as const;

/** Font family for the card text. The .woff2 is loaded via @font-face in index.css. */
export const NAME_FONT_FAMILY = 'Display Robust';

export type Platform = 'instagram' | 'soundcloud';
export type Variant = 'guest' | 'resident';

export interface Rect {
  x: number;
  y: number;
  width: number;
  height: number;
}

/** A navy panel + orange border holding a single centered line of text. */
export interface TextPanel {
  box: Rect;
  fill: string;
  stroke: string;
  strokeWidth: number;
  fontSize: number;
  fontFamily: string;
  color: string;
  align: 'left' | 'center' | 'right';
  letterSpacing: number;
}

export interface CardTemplate {
  id: string;
  platform: Platform;
  variant: Variant;
  /** Label for the platform (shared across variants). */
  platformLabel: string;
  /** Who is allowed to pick this template. Artists only see `artist` templates. */
  audience: 'artist' | 'internal';
  /** Frame size in logical px. Export is this * 2. */
  canvas: { width: number; height: number };
  assets: {
    back: string;
    logo: string;
    logoRect: Rect;
  };
  /** Region the artist photo is clipped to. */
  photoFrame: Rect;
  /** Orange frame drawn on top of the photo (Figma stroke align INSIDE). */
  border: { color: string; width: number };
  /** Artist-name panel (bottom) + its connector line to the photo. */
  namePanel: {
    box: Rect;
    fill: string;
    stroke: string;
    strokeWidth: number;
    connector: { x: number; y: number; length: number };
  };
  /** Artist name text style + bounds. */
  nameText: {
    box: Rect;
    fontSize: number;
    fontFamily: string;
    color: string;
    align: 'left' | 'center' | 'right';
    letterSpacing: number;
  };
  /** Resident only: show/programme name banner at the top. */
  showName?: TextPanel;
}

/** Shared artist-name panel for the Instagram layouts (identical guest/resident). */
const IG_NAME_PANEL: CardTemplate['namePanel'] = {
  box: { x: 646, y: 1241, width: 329, height: 106 },
  fill: COLORS.navy,
  stroke: COLORS.orange,
  strokeWidth: 2,
  connector: { x: 809, y: 1181, length: 64 },
};
const IG_NAME_TEXT: CardTemplate['nameText'] = {
  box: { x: 648, y: 1243, width: 324, height: 104 },
  fontSize: 36,
  fontFamily: NAME_FONT_FAMILY,
  color: COLORS.orange,
  align: 'center',
  letterSpacing: 0,
};

/**
 * SoundCloud shows the artwork as a small thumbnail in the stream (~50-160px),
 * where the Figma-sized panel (292x95 / 32px on a 1350px card) was unreadable.
 * The panel now fills the strip below the photo, from just right of the logo
 * (which ends at x=533) to the photo's right edge, at ~4x the type size. The
 * name auto-shrinks to fit, so long names still land on one line.
 */
const SC_NAME_PANEL: CardTemplate['namePanel'] = {
  box: { x: 560, y: 1168, width: 653, height: 145 },
  fill: COLORS.navy,
  stroke: COLORS.orange,
  strokeWidth: 4,
  connector: { x: 886, y: 1138, length: 30 },
};
const SC_NAME_TEXT: CardTemplate['nameText'] = {
  box: { x: 563, y: 1171, width: 647, height: 139 },
  fontSize: 120,
  fontFamily: NAME_FONT_FAMILY,
  color: COLORS.orange,
  align: 'center',
  letterSpacing: 0,
};

const showBanner = (box: Rect): TextPanel => ({
  box,
  fill: COLORS.navy,
  stroke: COLORS.orange,
  strokeWidth: 2,
  fontSize: 32,
  fontFamily: NAME_FONT_FAMILY,
  color: COLORS.orange,
  align: 'center',
  letterSpacing: 0,
});

const instagramGuest: CardTemplate = {
  id: 'instagram',
  platform: 'instagram',
  variant: 'guest',
  platformLabel: 'Instagram (4:5)',
  audience: 'artist',
  canvas: { width: 1080, height: 1440 },
  assets: {
    back: '/card-editor/instagram/back.png',
    logo: '/card-editor/instagram/logo.png',
    logoRect: { x: 38, y: 976, width: 389, height: 326 },
  },
  photoFrame: { x: 54, y: 103, width: 972, height: 1078 },
  border: { color: COLORS.orange, width: 7 },
  namePanel: IG_NAME_PANEL,
  nameText: IG_NAME_TEXT,
};

const instagramResident: CardTemplate = {
  ...instagramGuest,
  id: 'instagram-resident',
  variant: 'resident',
  assets: {
    ...instagramGuest.assets,
    back: '/card-editor/instagram/back-resident.png',
    logo: '/card-editor/instagram/logo-resident.png',
  },
  // Resident uses a thinner border; navy fills become grey.
  border: { color: COLORS.orange, width: 2 },
  namePanel: { ...IG_NAME_PANEL, fill: COLORS.grey },
  showName: { ...showBanner({ x: 38, y: 70, width: 571, height: 100 }), fill: COLORS.grey },
};

const soundcloudGuest: CardTemplate = {
  id: 'soundcloud',
  platform: 'soundcloud',
  variant: 'guest',
  platformLabel: 'SoundCloud (square)',
  // Artists build the SoundCloud post alongside the Instagram one from the same
  // show photo, so both platform cards are artist-facing.
  audience: 'artist',
  canvas: { width: 1350, height: 1350 },
  assets: {
    back: '/card-editor/soundcloud/back.png',
    logo: '/card-editor/soundcloud/logo.png',
    logoRect: { x: 38, y: 976, width: 495, height: 325 },
  },
  photoFrame: { x: 137, y: 62, width: 1076, height: 1076 },
  border: { color: COLORS.orange, width: 7 },
  namePanel: SC_NAME_PANEL,
  nameText: SC_NAME_TEXT,
};

const soundcloudResident: CardTemplate = {
  ...soundcloudGuest,
  id: 'soundcloud-resident',
  variant: 'resident',
  assets: {
    ...soundcloudGuest.assets,
    back: '/card-editor/soundcloud/back-resident.png',
    logo: '/card-editor/soundcloud/logo-resident.png',
  },
  border: { color: COLORS.orange, width: 2 },
  namePanel: { ...SC_NAME_PANEL, fill: COLORS.grey },
  showName: { ...showBanner({ x: 104, y: 36, width: 571, height: 95 }), fill: COLORS.grey },
};

export const CARD_TEMPLATES: CardTemplate[] = [
  instagramGuest,
  instagramResident,
  soundcloudGuest,
  soundcloudResident,
];

export function getTemplate(id: string): CardTemplate | undefined {
  return CARD_TEMPLATES.find((t) => t.id === id);
}

/** Distinct platforms present in a template list, in first-seen order. */
export function platformsOf(templates: CardTemplate[]): { platform: Platform; label: string }[] {
  const seen = new Map<Platform, string>();
  for (const t of templates) if (!seen.has(t.platform)) seen.set(t.platform, t.platformLabel);
  return [...seen].map(([platform, label]) => ({ platform, label }));
}

/** Resolve a template by platform + variant, falling back to the platform's guest / first. */
export function resolveTemplate(
  templates: CardTemplate[],
  platform: Platform,
  variant: Variant,
): CardTemplate | undefined {
  return (
    templates.find((t) => t.platform === platform && t.variant === variant) ??
    templates.find((t) => t.platform === platform) ??
    templates[0]
  );
}

/**
 * The cards an artist saves for their own show: one per platform, in the layout
 * variant the show calls for. Stories stay out of it — that frame is a multi-show
 * line-up built by an admin on the Social Cards page.
 */
export function showCardTemplates(variant: Variant): CardTemplate[] {
  return platformsOf(CARD_TEMPLATES.filter((t) => t.audience === 'artist'))
    .map(({ platform }) => resolveTemplate(CARD_TEMPLATES, platform, variant))
    .filter((t): t is CardTemplate => Boolean(t));
}
